/**
 * Shared grid engine for editable post grids (CommunityPostCreate, GearSwapPostCreate).
 * Fixed GRID_COLS x GRID_ROWS, coordinate-based items { id, type, value, x, y, w, h }.
 */

export const GRID_COLS = 3;
export const GRID_ROWS = 5;
export const EMPTY_WIDGET_TYPE = 'empty';

export function createEmptyGridCell(id, x, y, overrides = {}) {
  return {
    id,
    type: EMPTY_WIDGET_TYPE,
    value: '',
    x,
    y,
    w: 1,
    h: 1,
    ...overrides,
  };
}

function createEmptyGrid() {
  return Array.from({ length: GRID_ROWS }, () => Array(GRID_COLS).fill(null));
}

function canPlace(grid, item, x, y) {
  if (x + item.w > GRID_COLS) return false;
  if (y + item.h > GRID_ROWS) return false;
  for (let row = y; row < y + item.h; row++) {
    for (let col = x; col < x + item.w; col++) {
      if (grid[row][col] !== null) return false;
    }
  }
  return true;
}

function placeItem(grid, item) {
  for (let row = item.y; row < item.y + item.h; row++) {
    for (let col = item.x; col < item.x + item.w; col++) {
      grid[row][col] = item.id;
    }
  }
}

export function buildOccupancyGrid(items, ignoreId = null) {
  const grid = createEmptyGrid();
  for (const item of items) {
    if (item.id === ignoreId) continue;
    placeItem(grid, item);
  }
  return grid;
}

export function buildGridFromItems(items) {
  const grid = Array.from({ length: GRID_ROWS }, () => Array(GRID_COLS).fill(null));
  const byPosition = (items || []).slice().sort((a, b) => a.y !== b.y ? a.y - b.y : a.x - b.x);
  for (const item of byPosition) {
    grid[item.y][item.x] = { type: 'start', item };
    for (let dr = 0; dr < item.h; dr++) {
      for (let dc = 0; dc < item.w; dc++) {
        if (dr !== 0 || dc !== 0) grid[item.y + dr][item.x + dc] = { type: 'covered', item };
      }
    }
  }
  return grid;
}

export function moveItem(items, movingId, newX, newY) {
  const moving = items.find(i => i.id === movingId);
  if (!moving) return items;
  const occupancy = buildOccupancyGrid(items, movingId);
  if (canPlace(occupancy, moving, newX, newY)) {
    return items.map(i => (i.id === movingId ? { ...i, x: newX, y: newY } : i));
  }
  const occupant = items.find(i => i.id !== movingId && i.x === newX && i.y === newY);
  if (!occupant) return items;
  const moveTo = { x: Math.min(occupant.x, GRID_COLS - moving.w), y: Math.min(occupant.y, GRID_ROWS - moving.h) };
  const occupantTo = { x: Math.min(moving.x, GRID_COLS - occupant.w), y: Math.min(moving.y, GRID_ROWS - occupant.h) };
  return items.map(i => {
    if (i.id === movingId) return { ...i, x: moveTo.x, y: moveTo.y };
    if (i.id === occupant.id) return { ...i, x: occupantTo.x, y: occupantTo.y };
    return i;
  });
}

/**
 * Pack items into the grid top-to-bottom, left-to-right, respecting w/h.
 * Larger items first. Used to reflow after a drag.
 */
export function reflowItems(items) {
  const bySizeThenPos = (items || []).slice().sort((a, b) => {
    const areaA = (a.w || 1) * (a.h || 1);
    const areaB = (b.w || 1) * (b.h || 1);
    if (areaA !== areaB) return areaB - areaA;
    if (a.y !== b.y) return a.y - b.y;
    if (a.x !== b.x) return a.x - b.x;
    return String(a.id).localeCompare(String(b.id));
  });
  const grid = createEmptyGrid();
  const placed = [];
  for (const orig of bySizeThenPos) {
    const item = { ...orig };
    let placedHere = false;
    for (let y = 0; y < GRID_ROWS && !placedHere; y++) {
      for (let x = 0; x < GRID_COLS && !placedHere; x++) {
        if (canPlace(grid, item, x, y)) {
          item.x = x;
          item.y = y;
          placeItem(grid, item);
          placed.push(item);
          placedHere = true;
        }
      }
    }
    if (!placedHere) placed.push(item);
  }
  return placed.sort((a, b) => (a.y !== b.y ? a.y - b.y : a.x - b.x));
}

/**
 * Reflow items so they pack into the grid, but keep the item with fixedId at its current position.
 */
export function reflowItemsWithFixed(items, fixedId) {
  const fixed = items.find(i => i.id === fixedId);
  if (!fixed) return reflowItems(items);
  const grid = createEmptyGrid();
  placeItem(grid, fixed);
  const rest = items.filter(i => i.id !== fixedId);
  const bySizeThenPos = rest.slice().sort((a, b) => {
    const areaA = (a.w || 1) * (a.h || 1);
    const areaB = (b.w || 1) * (b.h || 1);
    if (areaA !== areaB) return areaB - areaA;
    if (a.y !== b.y) return a.y - b.y;
    if (a.x !== b.x) return a.x - b.x;
    return String(a.id).localeCompare(String(b.id));
  });
  const placed = [fixed];
  for (const orig of bySizeThenPos) {
    const item = { ...orig };
    let placedHere = false;
    for (let y = 0; y < GRID_ROWS && !placedHere; y++) {
      for (let x = 0; x < GRID_COLS && !placedHere; x++) {
        if (canPlace(grid, item, x, y)) {
          item.x = x;
          item.y = y;
          placeItem(grid, item);
          placed.push(item);
          placedHere = true;
        }
      }
    }
    if (!placedHere) placed.push(item);
  }
  return placed.sort((a, b) => (a.y !== b.y ? a.y - b.y : a.x - b.x));
}

/**
 * Reflow keeping all merged cells (w>1 or h>1) at their current positions.
 * Only 1x1 cells are repacked into remaining space (top-left, row by row).
 * The item with movingId is already at its new position in items; all other merged stay put.
 */
export function reflowSinglesKeepingMerged(items, movingId) {
  const moving = items.find(i => i.id === movingId);
  if (!moving) return reflowItems(items);
  const grid = createEmptyGrid();
  placeItem(grid, moving);
  const merged = items.filter(i => i.id !== movingId && ((i.w || 1) > 1 || (i.h || 1) > 1));
  for (const item of merged) {
    if (canPlace(grid, item, item.x, item.y)) {
      placeItem(grid, item);
    }
  }
  const singles = items.filter(i => i.id !== movingId && (i.w || 1) === 1 && (i.h || 1) === 1);
  const byPos = singles.slice().sort((a, b) => (a.y !== b.y ? a.y - b.y : a.x - b.x));
  const placedSingles = [];
  for (const orig of byPos) {
    const item = { ...orig };
    let placedHere = false;
    for (let y = 0; y < GRID_ROWS && !placedHere; y++) {
      for (let x = 0; x < GRID_COLS && !placedHere; x++) {
        if (canPlace(grid, item, x, y)) {
          item.x = x;
          item.y = y;
          placeItem(grid, item);
          placedSingles.push(item);
          placedHere = true;
        }
      }
    }
    if (!placedHere) placedSingles.push(item);
  }
  const result = [moving, ...merged, ...placedSingles];
  return result.sort((a, b) => (a.y !== b.y ? a.y - b.y : a.x - b.x));
}

/**
 * Place the moving item at (targetX, targetY) and reflow: merged cells stay put, only 1x1s reflow.
 * Returns null if drop target is a merged cell (invalid).
 */
export function placeAndReflow(items, movingId, targetX, targetY) {
  const moving = items.find(i => i.id === movingId);
  if (!moving) return null;
  const w = moving.w || 1;
  const h = moving.h || 1;
  if (targetX + w > GRID_COLS || targetY + h > GRID_ROWS) return null;
  const occupantAtTarget = items.find(i => i.id !== movingId && i.x === targetX && i.y === targetY);
  if (occupantAtTarget && ((occupantAtTarget.w || 1) > 1 || (occupantAtTarget.h || 1) > 1)) return null;
  const withMoved = items.map(i => (i.id === movingId ? { ...i, x: targetX, y: targetY } : i));
  return reflowSinglesKeepingMerged(withMoved, movingId);
}

/**
 * Split the grid's rows into bands: runs of consecutive rows that no item straddles.
 * e.g. [3x2] / [1x1 1x1 1x1] / [3x1] → bands of rows [0,1], [2], [3], ...
 */
export function getRowBands(items) {
  const bands = [];
  let start = 0;
  for (let y = 1; y <= GRID_ROWS; y++) {
    const straddled = y < GRID_ROWS && (items || []).some(i => i.y < y && i.y + (i.h || 1) > y);
    if (!straddled) {
      bands.push({ start, end: y }); // end exclusive
      start = y;
    }
  }
  return bands;
}

/**
 * Move a full-width item by row: its band takes the place of the band containing targetRow,
 * and the bands in between shift to fill. Returns null if the item isn't full width or nothing moves.
 */
export function moveRowBand(items, movingId, targetRow) {
  const moving = items.find(i => i.id === movingId);
  if (!moving || (moving.w || 1) !== GRID_COLS) return null;
  const bands = getRowBands(items);
  const from = bands.findIndex(b => moving.y >= b.start && moving.y < b.end);
  const to = bands.findIndex(b => targetRow >= b.start && targetRow < b.end);
  if (from === -1 || to === -1 || from === to) return null;
  const order = bands.slice();
  const [band] = order.splice(from, 1);
  order.splice(to, 0, band);
  // Shift each item by how far its band moved.
  const shiftByOldStart = new Map();
  let row = 0;
  for (const b of order) {
    shiftByOldStart.set(b.start, row - b.start);
    row += b.end - b.start;
  }
  return items
    .map(i => {
      const b = bands.find(bb => i.y >= bb.start && i.y < bb.end);
      return { ...i, y: i.y + shiftByOldStart.get(b.start) };
    })
    .sort((a, b) => (a.y !== b.y ? a.y - b.y : a.x - b.x));
}

export function getRowHeightMultipliers() {
  return Array(GRID_ROWS).fill(1);
}

/**
 * Border radius for a grid cell (px).
 * - `cellBorderRadiusPercent` (0–100): maps to 0 … half the shorter side (so 100% = pill-cap on the short axis).
 * - Legacy `cellBorderRadius` (px): used when percent is unset.
 */
export function resolveCellBorderRadiusPx(item, cellWidthPx, cellHeightPx) {
  const w = Number(cellWidthPx);
  const h = Number(cellHeightPx);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return undefined;
  const minSide = Math.min(w, h);
  const maxR = minSide * 0.5;
  const pct = item?.cellBorderRadiusPercent;
  if (typeof pct === 'number' && !Number.isNaN(pct)) {
    const clamped = Math.max(0, Math.min(100, pct));
    if (clamped <= 0) return undefined;
    return (clamped / 100) * maxR;
  }
  const legacy = item?.cellBorderRadius;
  if (typeof legacy === 'number' && !Number.isNaN(legacy) && legacy > 0) {
    return legacy;
  }
  return undefined;
}
